import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  IconButton
} from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useNotification } from "../hooks/useNotification.js";
import { MessageAPIService, UserApiService } from "../services/api";
import { PLACEHOLDER } from "../shared/constants";
import Log from "../shared/logger.js";
import { formatDateTime, isNum } from "../shared/utils";
import { useWebSocket } from "../shared/ws";
import ConfirmationDialog from "./common/ConfirmationDialog.js";
import MessageDownloader from "./common/DownloadMessages.js";
import { CloseCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const UserConversation = ({ initialPhone = "" }) => {
  const { notifyError, notifySuccess } = useNotification();
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [phone, setPhone] = useState(initialPhone);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSendingMsg, setIsSendingMsg] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState(new Set());
  const [maxPage, setMaxPage] = useState(1);
  const [noMoreOldMessages, setNoMoreOldMessages] = useState(false);
  const conversationRef = useRef(null);
  const [customMessage, setCustomMessage] = useState("");
  const [metadata, setMetadata] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [openPhoneNumberModal, setOpenPhoneNumberModal] = useState(
    !Boolean(initialPhone)
  );
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const PAGE_SIZE = 10;

  useWebSocket({
    url: process.env.REACT_APP_WS_URL,
    onEvent: onMessageReceived,
    event: "",
    phone,
  });

  // Load initial messages (most recent page)
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!phone || metadata) return;

      try {
        // Step 1: Fetch page 1 to get metadata
        const response = await UserApiService.userConversation(phone, 1);
        const meta = response.meta || {};
        setMetadata(meta);

        let calculatedMaxPage = Math.max(1, meta.pages || 1);
        setMaxPage(calculatedMaxPage);

        // Step 2: If it's the last page and may have < PAGE_SIZE messages, double the page size
        const lastPageSize = meta.total % PAGE_SIZE || PAGE_SIZE;
        let effectivePageSize = PAGE_SIZE;

        if (lastPageSize < PAGE_SIZE) {
          effectivePageSize = PAGE_SIZE + lastPageSize;
          --calculatedMaxPage;
        }

        const lastPageResponse = await UserApiService.userConversation(
          phone,
          calculatedMaxPage,
          effectivePageSize
        );

        setMessages(lastPageResponse.messages || []);
        setLoadedPages(new Set([calculatedMaxPage]));
        setCurrentPage(calculatedMaxPage);
        setInitialLoaded(true);

        // Auto-scroll to bottom
        setTimeout(() => {
          if (conversationRef.current) {
            conversationRef.current.scrollTop =
              conversationRef.current.scrollHeight;
          }
        }, 100);
      } catch (error) {
        Log.e("Error loading initial messages:", error);
      }
    };

    loadInitialMessages();
  }, [phone]);

  const handleScroll = useCallback(() => {
    if (!conversationRef.current || loading || !initialLoaded) return;

    const { scrollTop, scrollHeight, clientHeight } = conversationRef.current;

    // When scrolling up (near top), fetch older messages
    if (scrollTop < 50 && currentPage > 1 && !noMoreOldMessages) {
      const prevPage = currentPage - 1;
      if (!loadedPages.has(prevPage)) {
        fetchOlderMessages(prevPage);
      }
    }

    // When scrolling down (near bottom), fetch newer messages
    if (
      scrollHeight - (scrollTop + clientHeight) < 50 &&
      currentPage < maxPage
    ) {
      const nextPage = currentPage + 1;
      if (!loadedPages.has(nextPage)) {
        fetchNewerMessages(nextPage);
      }
    }
  }, [
    loading,
    currentPage,
    maxPage,
    loadedPages,
    noMoreOldMessages,
    initialLoaded,
  ]);

  useEffect(() => {
    const conversationEl = conversationRef.current;
    if (conversationEl) {
      conversationEl.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (conversationEl) {
        conversationEl.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchMessages = async (page) => {
    if (!phone) return [];

    setLoading(true);
    try {
      const response = await UserApiService.userConversation(phone, page);
      if (response.meta) {
        setMetadata(response.meta);
      }
      setLoading(false);
      return response.messages || [];
    } catch (err) {
      Log.e("Error fetching messages:", err);
      setLoading(false);
      return [];
    }
  };

  const fetchOlderMessages = async (page) => {
    if (loadedPages.has(page) || loading) return;

    const oldMessages = await fetchMessages(page);
    if (oldMessages.length > 0) {
      const oldScrollHeight = conversationRef.current?.scrollHeight || 0;

      setMessages((prevMessages) => [...oldMessages, ...prevMessages]);
      setLoadedPages((prev) => new Set(prev).add(page));
      setCurrentPage(page);

      // Maintain scroll position when adding content at the top
      setTimeout(() => {
        if (conversationRef.current) {
          const newScrollHeight = conversationRef.current.scrollHeight;
          conversationRef.current.scrollTop = newScrollHeight - oldScrollHeight;
        }
      }, 10);
    } else {
      setNoMoreOldMessages(true);
    }
  };

  const fetchNewerMessages = async (page) => {
    if (loadedPages.has(page) || loading) return;

    const newMessages = await fetchMessages(page);
    if (newMessages.length > 0) {
      setMessages((prevMessages) => [...prevMessages, ...newMessages]);
      setLoadedPages((prev) => new Set(prev).add(page));
      setCurrentPage(page);
    }
  };

  const refreshMessages = async () => {
    if (!phone) return;

    setLoading(true);
    try {
      // Fetch first page to get updated metadata
      const response = await UserApiService.userConversation(phone, 1);
      const meta = response.meta || {};
      setMetadata(meta);

      const latestMaxPage = Math.max(1, meta.pages || 1);
      setMaxPage(latestMaxPage);

      // Fetch the latest page
      if (latestMaxPage > 1) {
        const lastPageResponse = await UserApiService.userConversation(
          phone,
          latestMaxPage
        );
        setMessages(lastPageResponse.messages || []);
      } else {
        setMessages(response.messages || []);
      }

      setLoadedPages(new Set([latestMaxPage]));
      setCurrentPage(latestMaxPage);
      setInitialLoaded(true);
      setNoMoreOldMessages(false);

      // Auto-scroll to bottom
      setTimeout(() => {
        if (conversationRef.current) {
          conversationRef.current.scrollTop =
            conversationRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      Log.e("Error refreshing messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!customMessage.trim() && selectedImages.length === 0) {
      notifyError("Please enter custom message you want to send");
      return;
    }

    const base64Images = await Promise.all(
      selectedImages.map((file) => fileToBase64(file))
    );

    setIsSendingMsg(true);

    try {
      const payload = {
        From: phone,
        Body: customMessage,
        NumMedia: base64Images.length,
      };

      base64Images.forEach((base64, index) => {
        payload[`MediaUrl${index}`] = base64;
      });

      const code = process.env.REACT_APP_ON_MESSAGE_API_CODE;


      if (base64Images.length > 0) {
        const getImageUrl = await MessageAPIService.getImageURL(payload);

        getImageUrl?.saved_files?.map((file, index) => {
          payload[`MediaUrl${index}`] = file;
        })

      }
      const response = await MessageAPIService.sendMessage(payload, code);

      if (response.status !== 200) {
        Log.l("Error sending message:", response.data);
        return;
      }

      setCustomMessage("");
      setSelectedImages([]);
      setMessages((m) => [
        ...m,
        {
          phone,
          source: "user",
          body: customMessage,
          category: "general-chat",
          received_at: new Date().toISOString(),
          media_urls:base64Images
        },
      ]);
    } catch (error) {
      Log.e("Error sending message:", error);
    } finally {
      setIsSendingMsg(false);
    }
  };

  const handleKeyDown = (e, cb) => {
    if (e.key === "Enter") {
      e.preventDefault();
      cb();
    }
  };

  const handleResetFlow = async () => {
    try {
      const response = await UserApiService.resetFlow(phone);
      if (response.status === 200) {
        notifySuccess("Flow reset successful");
        setMessages([]);
        await refreshMessages();
      } else {
        notifyError("Failed to reset flow");
      }
    } catch (error) {
      Log.e("Error resetting flow:", error);
    }
  };

  const handlePhoneOnChange = useCallback((e) => setPhone(e.target.value.trim()), []);

  const handlePhoneSubmit = () => {
    if (phone) {
      setOpenPhoneNumberModal(false);
      setMessages([]);
      setLoadedPages(new Set());
      setInitialLoaded(false);
      setNoMoreOldMessages(false);
      refreshMessages();
    }
  };

  function onMessageReceived(message) {
    Log.l("Message received:", message);
    setMessages((m) => [...m, message]);
  }

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result); // base64 string
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Toolbar */}
      <Box sx={{ px: 2, py: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Using number: {phone}</Typography>
          <Box>
            {/* <MessageDownloader phone={phone} loading={loading} /> */}
            <Button
              variant="contained"
              sx={{
                mr: 1,
                color: "white",
                bgcolor: "#005CFF",
                textTransform: "none",
              }}
              onClick={refreshMessages}
              disabled={!phone || loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              sx={{
                mr: 1,
                color: "white",
                bgcolor: "#005CFF",
                textTransform: "none",
              }}
              onClick={() => setOpenPhoneNumberModal(true)}
            >
              Switch Phone Number
            </Button>
            <Button
              variant="contained"
              disabled={!phone}
              sx={{ color: "white", bgcolor: "#005CFF", textTransform: "none" }}
              onClick={() => setOpenConfirmationModal(true)}
            >
              Reset Flow
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Conversation Box */}
      <Box
        ref={conversationRef}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 2,
          py: 2,
          borderTop: "2px solid #ccc",
          borderBottom: "1px solid #ccc",
        }}
      >
        {loading && (
          <CircularProgress sx={{ display: "block", margin: "auto" }} />
        )}

        {noMoreOldMessages && (
          <Typography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            mb={2}
          >
            No older messages available.
          </Typography>
        )}

        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              textAlign: msg.source === "user" ? "left" : "right",
            }}
          >
            {/* Media Images */}
            {msg.media_urls?.length > 0 ? (
              <Box
                sx={{
                  display: "inline-block",
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: msg.source === "user" ? "#e1e1e1" : "#eee",
                  color: msg.source === "user" ? "white" : "black",
                  maxWidth: "80%",
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent:
                      msg.source === "user" ? "flex-start" : "flex-end",
                    mb: 1,
                  }}
                >
                  {msg.media_urls.map((url, idx) => {
                    const isVideo = /\.(mp4|webm|ogg|m4v|mov)$/i.test(url);
                    return (<Box
                      key={idx}
                      component={isVideo ? "video" : "img"}
                      src={url}
                      controls
                      muted
                      loop
                      alt={`media-${idx}`}
                      sx={{
                        width: "200px",
                        height: "auto",
                        borderRadius: 2,
                        objectFit: "fill",
                        border: "1px solid #ccc",
                      }}
                    />)
        })}
                </Box>
                {msg.body && (
                <Typography
                  sx={{
                    display: "inline-block",
                    p: 1,
                    borderRadius: "8px",
                    bgcolor: msg.source === "user" ? "#005CFF" : "#eee",
                    color: msg.source === "user" ? "white" : "black",
                    maxWidth: "100%",
                    textAlign: "left",
                  }}
                >
                  {msg.body}
                </Typography>
                )}
              </Box>
            )
          :
          msg.body && (
          <Typography
                  sx={{
                    display: "inline-block",
                    p: 1,
                    borderRadius: "8px",
                    bgcolor: msg.source === "user" ? "#005CFF" : "#eee",
                    color: msg.source === "user" ? "white" : "black",
                    maxWidth: "80%",
                    textAlign: "left",
                  }}
                >
                  {msg.body}
                </Typography>
          )
          }

            <Typography
              variant="caption"
              display="block"
              sx={{ fontStyle: "italic", color: "gray" }}
            >
              {msg?.source?.charAt(0)?.toUpperCase() + msg?.source?.slice(1)}{" "}
              sent on {formatDateTime(msg?.received_at)}
            </Typography>
          </Box>
        ))}

        {!messages.length && !loading && (
          <Typography
            variant="body2"
            color="textSecondary"
            textAlign="center"
            mt={2}
          >
            No messages available.
          </Typography>
        )}
      </Box>

      {/* Send message container */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: "#fff",
          borderTop: "1px solid #ccc",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {/* Image Previews */}
        {selectedImages.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {selectedImages.map((image, index) => (
              <Box
                key={index}
                sx={{ position: "relative", width: 80, height: 80 }}
              >
                <img
                  src={URL.createObjectURL(image)}
                  alt={`preview-${index}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  sx={{
                    position: "absolute",
                    top: -10,
                    right: -10,
                    backgroundColor: "#fff",
                    zIndex: 1,
                  }}
                  onClick={() => {
                    setSelectedImages((prev) =>
                      prev.filter((_, i) => i !== index)
                    );
                  }}
                >
                  <CloseCircleOutlined />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Input Row */}
        <Box sx={{ display: "flex", width: "100%", alignItems: "center" }}>
          <IconButton component="label" sx={{ mr: 1 }} disabled={!phone || loading}>
            <PlusCircleOutlined />
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setSelectedImages((prev) => [...prev, ...files]);
              }}
            />
          </IconButton>

          <TextField
            fullWidth
            sx={{ mr: 1 }}
            size="small"
            variant="outlined"
            value={customMessage}
            disabled={!phone || loading}
            placeholder={PLACEHOLDER.CUSTOME_MESSAGE}
            onKeyDown={(e) => handleKeyDown(e, handleSubmit)}
            onChange={(e) => setCustomMessage(e.target.value)}
          />
          <Button
            sx={{
              ml: 1,
              color: "white",
              bgcolor: "#005CFF",
              textTransform: "none",
            }}
            variant="contained"
            onClick={handleSubmit}
            disabled={!phone || loading || isSendingMsg}
          >
            Send
          </Button>
        </Box>
      </Box>

      {/* Phone number Modal */}
      <Dialog
        open={openPhoneNumberModal}
        onClose={() => setOpenPhoneNumberModal(false)}
      >
        <DialogTitle>Enter Phone Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type="tel"
            value={phone}
            error={!phone}
            margin="dense"
            label={"Phone Number"}
            onChange={handlePhoneOnChange}
            onKeyDown={(e) => handleKeyDown(e, handlePhoneSubmit)}
            helperText={
              !phone || !isNum(phone) ? "Enter a valid phone number" : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPhoneNumberModal(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ color: "white", bgcolor: "#005CFF" }}
            disabled={loading || !phone}
            onClick={handlePhoneSubmit}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        id="reset-user-flow"
        open={openConfirmationModal}
        title={"Reset User Flow"}
        description="Are you sure you want to reset the user flow? This action cannot be undone."
        onCancel={() => {
          setOpenConfirmationModal(false);
        }}
        onConfirm={() => {
          setOpenConfirmationModal(false);
          handleResetFlow();
        }}
        confirmText="Reset"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default UserConversation;
